"use client";

import { useState, useEffect, useRef } from "react";
import {
  X,
  ChevronRight,
  Calendar,
  Minus,
  Plus,
  Check,
  MapPin,
  Upload,
  Edit,
  Loader,
} from "lucide-react";
import { Button } from "./Button";
import { LocationInput } from "../location-picker";
import {
  getEventNeedsFromType,
  getEventTypes,
  getBusinessByNeeds,
} from "../../services/events";
import { successToast,errorToast } from '../../utils/toasters'

export default function EditEventForm({ event, onClose, onUpdate }) {
  // Initialize form data with all possible fields based on event type
  const initializeFormData = () => {
    const baseData = {
      eventType: event.eventType || "Personal Events",

      date: event.date ? event.date.split("T")[0] : "",
      venue: event.venue || "",
      description: event.description || "",
      // Initialize needs and vendors for all event types to prevent undefined errors
      needs: event.selectedEventNeeds
        ? event.selectedEventNeeds.split(",")
        : [],
      vendors: event.vendors || [],
      category: event.category || "personal",
    };

    if (event.category === "personal") {
      return {
        ...baseData,
        eventName: event.eventName || "",
        guests: event.guestCount || 2,
        budget: event.budget || 500,
      };
    } else if (event.category === "community") {
      return {
        ...baseData,
        eventName: event.name || "",
        startTime: event.eventstarttime || "",
        endTime: event.eventendtime || "",
        city: event.city || "",
        state: event.state || "",
        coverImage: event.cover_image || null,
        tempCommunityImage: null,
      };
    } else if (event.category === "commercial") {
      return {
        ...baseData,
        eventName: event.name || "",
        startTime: event.eventstarttime || "",
        endTime: event.eventendtime || "",

        city: event.city || "",
        state: event.state || "",
        coverImage: event.cover_image || null,
        tempCommunityImage: null,
      };
    }
    return baseData;
  };

  const [formData, setFormData] = useState(initializeFormData());
  const [originalData] = useState(initializeFormData());
  const [modalState, setModalState] = useState({ type: "none" });
  const [currentView, setCurrentView] = useState("details"); // details, needs, vendors
  const [isLoading, setIsLoading] = useState(false);

  // Add activeNeed state at the component level
  const [activeNeed, setActiveNeed] = useState("");

  // API Data
  const [eventTypes, setEventTypes] = useState([]);
  const [eventNeeds, setEventNeeds] = useState([]);
  const [vendors, setVendors] = useState({});
  const [businessData, setBusinessData] = useState([]);

  // Image upload ref
  const fileInputRef = useRef(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);


  // Load event types on mount
  useEffect(() => {
    fetchEventTypes();
  }, []);

  useEffect(() => {
    if (formData.category === "personal") {
      fetchEventNeeds();
    }
  }, [formData.category, eventTypes]);

  // Load vendors when needs change
  useEffect(() => {
    if (
      formData.eventType === "Personal Events" &&
      formData.needs &&
      formData.needs.length > 0
    ) {
      fetchVendors();
    }
  }, [formData.needs]);

  // Set activeNeed when needs change
  useEffect(() => {
    if (formData.needs && formData.needs.length > 0) {
      setActiveNeed(formData.needs[0]);
    } else {
      setActiveNeed("");
    }
  }, [formData.needs]);

  const fetchEventTypes = async () => {
    try {
      const response = await getEventTypes();
      setEventTypes(response.events || []);
    } catch (error) {
      console.error("Error fetching event types:", error);
    }
  };

  const fetchEventNeeds = async () => {
    try {
      const eventType = eventTypes.find(
        (e) => e.eventtypename === formData.eventType
      );
      if (eventType) {
        const response = await getEventNeedsFromType(eventType.id);
        setEventNeeds(response.eventneeds || []);
      }
    } catch (error) {
      console.error("Error fetching event needs:", error);
    }
  };

  const fetchVendors = async () => {
    try {
      setIsLoading(true);
      const response = await getBusinessByNeeds(formData.needs);
      const businessList = response.business || [];
      setBusinessData(businessList);

      // Organize vendors by service
      const vendorsByService = {};
      businessList.forEach((business) => {
        let servicesStr = business.services;
        if (servicesStr.startsWith('"') && servicesStr.endsWith('"')) {
          servicesStr = servicesStr.slice(1, -1);
        }
        const services = servicesStr
          .split(",")
          .map((service) => service.trim());

        services.forEach((service) => {
          if (!vendorsByService[service]) {
            vendorsByService[service] = [];
          }
          vendorsByService[service].push({
            id: business.id.toString(),
            businessname: business.businessname,
            businessimageurl: business.businessimageurl || "/placeholder.svg",
            businessaddress: business.businessaddress1,
          });
        });
      });

      setVendors(vendorsByService);
    } catch (error) {
      console.error("Error fetching vendors:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Check for changes
  const hasChanges = () => {
    const compareObjects = (obj1, obj2) => {
      if (typeof obj1 !== typeof obj2) return true;
      if (obj1 === null || obj2 === null) return obj1 !== obj2;
      if (typeof obj1 !== "object") return obj1 !== obj2;

      if (Array.isArray(obj1)) {
        if (!Array.isArray(obj2) || obj1.length !== obj2.length) return true;
        return obj1.some((item, index) => item !== obj2[index]);
      }

      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);
      if (keys1.length !== keys2.length) return true;

      return keys1.some((key) => compareObjects(obj1[key], obj2[key]));
    };

    return compareObjects(formData, originalData);
  };

  // Get only changed fields
  const getChangedFields = () => {
    const changes = {};
    const compareAndAdd = (currentObj, originalObj, prefix = "") => {
      Object.keys(currentObj).forEach((key) => {
        const fullKey = prefix ? `${prefix}.${key}` : key;
        const currentVal = currentObj[key];
        const originalVal = originalObj[key];

        if (
          typeof currentVal === "object" &&
          currentVal !== null &&
          !Array.isArray(currentVal)
        ) {
          compareAndAdd(currentVal, originalVal || {}, fullKey);
        } else if (JSON.stringify(currentVal) !== JSON.stringify(originalVal)) {
          changes[fullKey] = currentVal;
        }
      });
    };

    compareAndAdd(formData, originalData);
    return changes;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("contactInfo.")) {
      const field = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        contactInfo: {
          ...prev.contactInfo,
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({ ...prev, tempCommunityImage: file }));
      const imageUrl = URL.createObjectURL(file);
      setCoverImagePreview({
        url: imageUrl,
        name: file.name,
      });
    }
  };

  const handleRemoveImage = () => {
    setCoverImagePreview(null);
    setFormData((prev) => ({
      ...prev,
      tempCommunityImage: null,
      coverImage: null,
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const toggleNeed = (need) => {
    const updatedNeeds = formData.needs.includes(need)
      ? formData.needs.filter((n) => n !== need)
      : [...formData.needs, need];
    setFormData((prev) => ({ ...prev, needs: updatedNeeds }));
  };

  const toggleVendor = (vendorId) => {
    const updatedVendors = formData.vendors.includes(vendorId)
      ? formData.vendors.filter((id) => id !== vendorId)
      : [...formData.vendors, vendorId];
    setFormData((prev) => ({ ...prev, vendors: updatedVendors }));
  };

  const handleUpdate = () => {
    const changes = getChangedFields();
    changes["category"] = formData.category;

    // Remove needs and vendors if category is not "Personal"
    if (formData.category !== "Personal") {
      delete changes.needs;
      delete changes.vendors;
    }
    if (Object.keys(changes).length > 0) {
      onUpdate(event.id, changes);
    }
    onClose();
  };

  const handleGuestsChange = (amount) => {
    setFormData((prev) => ({
      ...prev,
      guests: Math.max(1, prev.guests + amount),
    }));
  };

  const handleBudgetChange = (amount) => {
    setFormData((prev) => ({
      ...prev,
      budget: Math.max(100, prev.budget + amount),
    }));
  };

  const showEventTypeModal = () => {
    setModalState({ type: "eventType" });
  };

  const handleSelectEventType = (eventType) => {
    setFormData((prev) => ({ ...prev, eventType: eventType }));
    setModalState({ type: "none" });
  };

  // Render different content based on event type and current view
  const renderContent = () => {
    // Event type selection modal
    if (modalState.type === "eventType") {
      return (
        <div className="p-4">
          <div className="flex items-center mb-4">
            <button
              onClick={() => setModalState({ type: "none" })}
              className="mr-3"
            >
              <ChevronRight size={20} className="rotate-180" />
            </button>
            <h2 className="text-lg font-medium">Select event type</h2>
          </div>
          <div className="mb-6">
            <div className="text-xs font-medium text-gray-500 uppercase mb-2">
              Event Types
            </div>
            <div className="bg-gray-50 rounded-lg">
              {eventTypes.map((type, index) => (
                <button
                  key={type.id}
                  className={`w-full px-4 py-3 text-left hover:bg-gray-100 transition-colors ${
                    index !== eventTypes.length - 1
                      ? "border-b border-gray-100"
                      : ""
                  }`}
                  onClick={() => handleSelectEventType(type.eventtypename)}
                >
                  {type.eventtypename}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Personal Events - Needs View
    if (formData.category === "personal" && currentView === "needs") {
      return (
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Edit Event Needs</h2>
            <button
              onClick={() => setCurrentView("details")}
              className="text-sm text-[#7B189F] hover:text-[#671485]"
            >
              Back to Details
            </button>
          </div>

          <div className="space-y-3">
            {!eventNeeds || eventNeeds.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No event needs available for this type.
              </p>
            ) : (
              eventNeeds.map((need) => (
                <div
                  key={need.id}
                  onClick={() => toggleNeed(need.eventneedsname)}
                  className={`flex items-center justify-between p-4 border rounded-lg cursor-pointer transition-colors ${
                    formData.needs?.includes(need.eventneedsname)
                      ? "border-green-500 bg-green-50"
                      : "hover:border-[#7B189F]"
                  }`}
                >
                  <div className="flex items-center">
                    <span className="mr-2">{need.image_url}</span>
                    <div>
                      <h3 className="font-medium">{need.eventneedsname}</h3>
                    </div>
                  </div>
                  <div
                    className={`w-6 h-6 flex items-center justify-center rounded-sm border ${
                      formData.needs?.includes(need.eventneedsname)
                        ? "bg-green-500 border-green-500"
                        : "border-gray-300"
                    }`}
                  >
                    {formData.needs?.includes(need.eventneedsname) && (
                      <Check size={16} className="text-white" />
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="flex justify-between mt-8">
            <Button variant="outline" onClick={() => setCurrentView("details")}>
              Back
            </Button>
            <Button
              onClick={() => setCurrentView("vendors")}
              className="bg-[#7B189F] hover:bg-[#671485] text-white"
              disabled={!formData.needs || formData.needs.length === 0}
            >
              {!formData.needs || formData.needs.length === 0
                ? "Select Needs First"
                : "Edit Vendors"}
            </Button>
          </div>
        </div>
      );
    }

    // Personal Events - Vendors View
    if (formData.category === "personal" && currentView === "vendors") {
      const selectedNeeds = formData.needs || [];

      return (
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Edit Vendors</h2>
            <button
              onClick={() => setCurrentView("needs")}
              className="text-sm text-[#7B189F] hover:text-[#671485]"
            >
              Back to Needs
            </button>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader className="animate-spin" size={32} />
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h3 className="text-xs font-medium text-gray-500 uppercase mb-2">
                  Selected event needs
                </h3>
                <div className="flex flex-wrap gap-2">
                  {selectedNeeds.map((need) => (
                    <button
                      key={need}
                      onClick={() => setActiveNeed(need)}
                      className={`px-4 py-2 rounded-full text-sm font-medium ${
                        activeNeed === need
                          ? "bg-[#7B189F] text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {need}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {!vendors[activeNeed] || vendors[activeNeed]?.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">
                    No vendors found for {activeNeed}.
                  </p>
                ) : (
                  vendors[activeNeed]?.map((vendor) => (
                    <div
                      key={vendor.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <img
                            src={vendor.businessimageurl}
                            alt={vendor.businessname}
                            className="w-12 h-12 mr-4 rounded-full"
                            required
                          />
                          <div>
                            <h4 className="font-medium">
                              {vendor.businessname}
                            </h4>
                            <p className="text-sm text-gray-500">
                              {vendor.businessaddress}
                            </p>
                          </div>
                        </div>
                        <div
                          onClick={() => toggleVendor(vendor.id)}
                          className={`w-6 h-6 flex items-center justify-center rounded-sm border cursor-pointer ${
                            formData.vendors?.includes(vendor.id)
                              ? "bg-gray-200 border-gray-300"
                              : "border-gray-300"
                          }`}
                        >
                          {formData.vendors?.includes(vendor.id) && (
                            <Check size={16} className="text-gray-600" />
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}

          <div className="flex justify-between mt-8">
            <Button variant="outline" onClick={() => setCurrentView("needs")}>
              Back
            </Button>
            <Button
              onClick={() => setCurrentView("details")}
              className="bg-[#7B189F] hover:bg-[#671485] text-white"
            >
              Done
            </Button>
          </div>
        </div>
      );
    }

    // Main details view for all event types
    return (
      <div className="p-4">
        <h2 className="text-xl font-bold mb-1">Edit Event Details</h2>
        <p className="text-gray-500 mb-6">Update your event information</p>

        <div className="space-y-4">
          {/* Common fields for all event types */}
          <div>
            <input
              name="eventName"
              value={formData.eventName}
              onChange={handleChange}
              placeholder="Event Name*"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
              required
            />
          </div>

          {/* Personal Events specific fields */}
          {formData.category === "personal" && (
            <>
              <div className="relative">
                <input
                  value={formData.eventType}
                  placeholder="Select Event Type*"
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10 cursor-pointer"
                  required
                  readOnly
                />
                <ChevronRight
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  size={20}
                />
              </div>
            </>
          )}

          <div className="relative">
            <input
              name="date"
              type="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent pr-10"
              required
            />
            <Calendar
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
              size={20}
            />
          </div>

          {/* Community & Commercial Events specific fields */}
          {(formData.category === "community" ||
            formData.category === "commercial") && (
            <>
              <div>
                <div className="uppercase text-xs font-medium text-gray-500 mb-2">
                  Start Time*
                </div>
                <input
                  name="startTime"
                  type="time"
                  value={formData.startTime}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                  onChange={handleChange}
                />
              </div>

              <div>
                <div className="uppercase text-xs font-medium text-gray-500 mb-2">
                  End Time*
                </div>
                <input
                  name="endTime"
                  type="time"
                  value={formData.endTime}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                  onChange={handleChange}
                />
              </div>

              <input
                name="city"
                type="text"
                value={formData.city}
                placeholder="City*"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                onChange={handleChange}
              />

              <input
                name="state"
                type="text"
                value={formData.state}
                placeholder="State*"
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                onChange={handleChange}
              />

              {/* Cover Image Upload */}
              <div>
                <div className="uppercase text-xs font-medium text-gray-500 mb-2">
                  Event Cover Image
                </div>
                <div
                  className={`bg-gray-100 rounded-lg relative border border-gray-200 shadow-sm ${
                    coverImagePreview || formData.coverImage
                      ? "p-0 overflow-hidden h-48"
                      : "p-8 flex flex-col items-center justify-center"
                  }`}
                >
                  <img
                    src={coverImagePreview?.url || formData.coverImage}
                    alt="Event cover"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </>
          )}

          <LocationInput
            value={formData.venue}
            onChange={(location) => {
              setFormData((prev) => ({ ...prev, venue: location }));
            }}
          />

          {/* Personal Events specific fields */}
          {formData.category === "personal" && (
            <>
              <div>
                <div className="uppercase text-xs font-medium text-gray-500 mb-2">
                  EXPECTED NUMBER OF GUESTS*
                </div>
                <div className="flex items-center">
                  <input
                    type="number"
                    name="guests"
                    value={formData.guests}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        guests: parseInt(e.target.value) || 1,
                      }))
                    }
                    className="flex-1 h-12 px-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                    min="1"
                  />
                  <button
                    type="button"
                    onClick={() => handleGuestsChange(-1)}
                    className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <Minus size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleGuestsChange(1)}
                    className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              <div>
                <div className="uppercase text-xs font-medium text-gray-500 mb-2">
                  PLANNED BUDGET*
                </div>
                <div className="flex items-center">
                  <div className="flex-1 relative">
                    <span className="absolute left-4 top-1/2 transform -translate-y-1/2">
                      $
                    </span>
                    <input
                      type="number"
                      name="budget"
                      value={formData.budget}
                      disabled
                      className="w-full h-12 pl-8 pr-4 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
                      min="100"
                      step="50"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleBudgetChange(-50)}
                    className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <Minus size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleBudgetChange(50)}
                    className="w-12 h-12 flex items-center justify-center border border-gray-200 rounded-lg ml-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              {/* Needs & Vendors Section */}
              <div className="border-t pt-4 mt-4">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-medium">Event Needs & Vendors</h3>
                  <button
                    onClick={() => setCurrentView("needs")}
                    className="text-sm text-[#7B189F] hover:text-[#671485] flex items-center"
                  >
                    <Edit size={14} className="mr-1" />
                    Edit Needs
                  </button>
                </div>

                {formData.needs.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No event needs selected
                  </p>
                ) : (
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-medium">Selected Needs:</span>{" "}
                      {formData.needs.join(", ")}
                    </div>
                    <div className="text-sm">
                      <span className="font-medium">Selected Vendors:</span>{" "}
                      {formData.vendors.length || 0} vendors
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          <div>
            <div className="uppercase text-xs font-medium text-gray-500 mb-2">
              MORE DESCRIPTION*
            </div>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Add a personal touch—share your event vibe to excite attendees!"
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7B189F] focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex justify-between mt-8">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdate}
            className="bg-[#7B189F] hover:bg-[#671485] text-white"
            disabled={!hasChanges()}
          >
            Update Event
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md mx-auto overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h1 className="text-xl font-bold">
            {modalState.type === "eventType"
              ? "Select event type"
              : currentView === "needs"
              ? "Edit Event Needs"
              : currentView === "vendors"
              ? "Edit Vendors"
              : "Edit Event"}
          </h1>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto max-h-[calc(100vh-150px)]">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
